import { useState, useEffect, type MouseEvent, type ComponentProps } from "react";
import { useNavigate } from "react-router-dom";
import { getRoles } from "../../../api/roles";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Button,
  Dialog,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SnackbarCustom, {
  type SnackbarData,
} from "../../../components/SnackBarCustom";
import type { RoleItem } from "../../../types/role";

type AuthorityColumn = {
  id: keyof RoleItem | "buttonSpace";
  label: string;
  minWidth: string;
  isSmallScreen: boolean;
  align?: "right";
  button?: boolean;
};

const columns: AuthorityColumn[] = [
  { id: "name", label: "権限名", minWidth: "120px", isSmallScreen: true },
  {
    id: "MBranchCount",
    label: "関連付け組織数",
    minWidth: "120px",
    isSmallScreen: false,
  },
  {
    id: "UserCount",
    label: "ユーザー数",
    minWidth: "120px",
    isSmallScreen: false,
  },
  {
    id: "buttonSpace",
    label: "",
    align: "right",
    minWidth: "120px",
    button: true,
    isSmallScreen: true,
  },
];

const getRoleCellValue = (
  role: RoleItem,
  columnId: AuthorityColumn["id"]
): string | number => {
  if (columnId === "buttonSpace") {
    return "";
  }
  const value = role[columnId];
  if (typeof value === "string" || typeof value === "number") {
    return value;
  }
  return value != null ? String(value) : "";
};

export default function AuthorityTable() {
  const navigate = useNavigate();

  const handleClick = (event: MouseEvent, path: string) => {
    event.stopPropagation();
    navigate(path);
  };

  const [selectedRow, setSelectedRow] = useState<RoleItem | null>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [roles, setRoles] = useState<RoleItem[]>([]);
  const [snackBarstatus, setSnackBarstatus] = useState<SnackbarData>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const fetchRoles = async () => {
    try {
      const response = await getRoles({});
      console.log("権限設定一覧取得", response);
      if (response.status === "ok") {
        setRoles(response.m_roles);
        setSnackBarstatus(response);
        setSnackbarOpen(true);
      }
      console.log("スナックバー表示されたよ");
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  useEffect(() => {
    void fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLocationCodeClick = (row: RoleItem) => {
    setSelectedRow(row);
    setIsPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
  };

  const buttonVaridate = (roleId: number) => {
    return (
      <Box sx={{ width: "100%", textAlign: "right" }}>
        <Button
          name="connectingButton"
          variant="contained"
          onClick={(event) =>
            handleClick(
              event,
              `/account/authority/connecting?role-id=${roleId}`
            )
          }
          sx={{ pointerEvents: "auto" }}
        >
          関連付け
        </Button>
      </Box>
    );
  };

  return (
    <>
      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
        <TableContainer>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    style={{ minWidth: column.minWidth }}
                    sx={{
                      color: "outline.dark",
                      fontWeight: "bold",
                      borderBottom: 2,
                      padding: "8px",
                      "@media screen and (max-width:900px)": {
                        display:
                          column.isSmallScreen === true ? "table-cell" : "none",
                      },
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map((role, index) => {
                return (
                  <TableRow
                    {...({
                      name: `role_${index}`,
                    } as ComponentProps<typeof TableRow>)}
                    hover={false}
                    role="checkbox"
                    tabIndex={-1}
                    key={role.id}
                    onClick={() => handleLocationCodeClick(role)}
                    sx={{
                      pointerEvents: "none",
                      "@media screen and (max-width:900px)": {
                        cursor: "pointer",
                        pointerEvents: "auto",
                      },
                    }}
                  >
                    {columns.map((column) => {
                      return (
                        <TableCell
                          key={column.id}
                          sx={{
                            padding: "8px",
                            "@media screen and (max-width:900px)": {
                              display:
                                column.isSmallScreen === true
                                  ? "table-cell"
                                  : "none",
                            },
                          }}
                        >
                          {column.button
                            ? buttonVaridate(role.id)
                            : getRoleCellValue(role, column.id)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Dialog open={isPopoverOpen} onClose={handlePopoverClose}>
          <Box
            sx={{
              display: "flex",
              height: "56px",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px",
              borderBottom: "2px solid #CCC",
              position: "sticky",
              top: 0,
              backgroundColor: "#fff",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                paddingLeft: "8px",
                fontWeight: "bold",
                fontSize: "20px",
              }}
            >
              災害情報詳細
            </Box>
            <IconButton
              aria-label="close"
              onClick={handlePopoverClose}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Table sx={{ WebkitWritingMode: "horizontal-tb" }}>
            <TableBody>
              {selectedRow &&
                columns.map((column) => {
                  return (
                    <TableRow role="checkbox" tabIndex={-1} key={column.id}>
                      <TableCell
                        sx={{
                          color: "outline.dark",
                          fontWeight: "bold",
                          boxSizing: "border-box",
                          width: "calc((100vw - 64px) / 2)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            WebkitWritingMode: "horizontal-tb",
                            height: "100%",
                            padding: "8px",
                          }}
                        >
                          {column.label}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          writingMode: "horizontal-tb",
                          boxSizing: "border-box",
                          width: "calc((100vw - 64px) / 2)",
                          overflowWrap: "break-word",
                        }}
                      >
                        {column.button
                          ? buttonVaridate(selectedRow.id)
                          : getRoleCellValue(selectedRow, column.id)}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </Dialog>
        <SnackbarCustom
          data={snackBarstatus}
          openStatus={snackbarOpen}
          onClose={() => setSnackbarOpen(false)}
        />
      </Paper>
    </>
  );
}
